import { LanguageMenu } from './language.tsx';
import { useScreen } from './scope.ts';
import { strings } from './strings.ts';
import { initialsOf } from './ui/avatar.tsx';

/** Whose page it is: a brand, or the group, as its record names it. A feature reads it; the kit only draws it. */
export interface Face {
  readonly name: string;
  /** A short mark, like KM; the name's initials (two at most) when there is none. */
  readonly mark?: string;
  readonly logoUrl?: string;
}

/** The face's logo, else its mark, as a badge. `onBand`: white, on the brand's red band. */
export function FaceMark({ face, small = false, onBand = false, tile = false }: { readonly face: Face; readonly small?: boolean; readonly onBand?: boolean; /** The brand's red tile, white letters (the sidebar's head). */ readonly tile?: boolean }) {
  const kit = useScreen() !== null;
  if (face.logoUrl !== undefined)
    return <img className={kit ? (onBand ? 'h-s7 rounded-control bg-surface object-contain p-s1' : 'size-s6 rounded-control object-contain') : `brand-logo${small ? ' brand-logo-small' : ''}`} src={face.logoUrl} alt={face.name} />;
  const look = tile
    ? 'inline-flex size-s7 shrink-0 items-center justify-center rounded-control bg-brand-field px-s1 font-brand text-strong leading-none font-extrabold text-on-brand'
    : onBand
    ? 'inline-flex h-s7 min-w-s7 items-center justify-center rounded-control bg-surface px-s2 font-brand text-heading leading-none font-extrabold text-brand-field'
    : 'inline-flex h-s6 min-w-s6 items-center justify-center rounded-control bg-brand-soft px-s1 text-caption font-bold text-brand';
  return (
    <span className={kit ? look : `brand-mark${small ? ' brand-mark-small' : ''}`} role="img" aria-label={face.name}>
      {face.mark ?? initialsOf(face.name).toUpperCase()}
    </span>
  );
}

/** makan os's mark (MIX.md, "Logo for the mix"): a bowl being typed by the system, on the brand square. */
export function ProductMark() {
  return (
    <svg viewBox="0 0 48 48" width="28" height="28" aria-hidden="true" className="shrink-0">
      <rect width="48" height="48" rx="11" fill="var(--brand-field)" />
      <path d="M8 24h24a12 12 0 0 1-24 0z" fill="var(--on-brand)" />
      <rect x="34" y="14" width="6" height="22" rx="1" fill="var(--on-brand)" />
    </svg>
  );
}

/** The bar on the public pages: the brand (or the group) the person came through, its mark and name, and what the page is (`sub`) under the name, smaller and lighter in weight but full white, for contrast on the red. Before it is known, the sub alone. */
export function BrandBar({ face, sub }: { readonly face: Face | null; readonly sub: string }) {
  if (useScreen() !== null)
    return (
      <header className="bg-brand-field text-on-brand print:hidden">
        <div className="mx-auto flex min-h-s8 w-full max-w-[30rem] items-center gap-s3 px-s4 py-s2">
          {face !== null && <FaceMark face={face} onBand />}
          <span className="flex min-w-0 flex-col leading-tight">
            {face !== null && <span className="truncate font-semibold">{face.name}</span>}
            <span className={face === null ? 'font-semibold' : 'text-caption'}>{sub}</span>
          </span>
          <span className="ml-auto flex">
            <LanguageMenu onBand />
          </span>
        </div>
      </header>
    );
  return (
    <header className="brand-bar">
      {face !== null && <FaceMark face={face} />}
      {face !== null && <span className="brand-sub">{face.name}</span>}
      <span className="brand-sub">{sub}</span>
      <span className="ml-auto flex">
        <LanguageMenu onBand />
      </span>
    </header>
  );
}

/** The command center's name, with the group's mark once a page knows the group. */
export function ProductName({ group = null }: { readonly group?: Face | null }) {
  if (useScreen() === null)
    return (
      <p className="product-name">
        {group !== null && <FaceMark face={group} small />}
        {strings.staff.product}
      </p>
    );
  return (
    <p className="flex items-center gap-s2 text-strong font-semibold text-ink">
      <ProductMark />
      <span>{strings.staff.product}</span>
      {group !== null && <FaceMark face={group} small />}
    </p>
  );
}

/** The bar above a staff page outside the staff frame (sign-in): the product's mark and name. A signed-in page is drawn in the frame instead (Screen's `staff`). */
export function StaffBar() {
  return (
    <header className="flex h-topbar items-center justify-between gap-s3 border-b border-line bg-surface px-s4 print:hidden">
      <ProductName />
      <LanguageMenu />
    </header>
  );
}
